import { useEffect, useState, useCallback } from "react";
import { api, BACKEND, formatApiErrorDetail } from "@/lib/api";
import { CheckCircle2, RotateCcw, Mail, Phone, Search, Download, Gift, TrendingUp, Clock, Users, Plus, X, Save } from "lucide-react";

const STATUS_LABEL = {
  pending: { label: "En attente", color: "text-[#697585] border-[#D9D3C5] bg-[#F6F0E7]" },
  active: { label: "Active", color: "text-[#5A7A5A] border-[#BDC3BC] bg-[#BDC3BC]/20" },
  used: { label: "Utilisée", color: "text-[#697585] border-[#697585]/30 bg-[#697585]/10" },
  cancelled: { label: "Annulée", color: "text-red-600 border-red-200 bg-red-50" },
};

export default function GiftCardsAdmin() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total_sold: 0, total_revenue: 0, used: 0, active: 0, usage_rate: 0 });
  const [filter, setFilter] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState("");
  const [manual, setManual] = useState(null);
  const [manualErr, setManualErr] = useState("");
  const [manualSaving, setManualSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter) params.status_filter = filter;
      if (search.trim()) params.search = search.trim();
      const [list, st] = await Promise.all([
        api.get("/admin/giftcards", { params }),
        api.get("/admin/giftcards/stats"),
      ]);
      setItems(list.data || []);
      setStats(st.data || stats);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [filter, search]); // eslint-disable-line

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const onSearch = (e) => { e.preventDefault(); load(); };

  const emptyManual = {
    amount: 100,
    beneficiary_name: "",
    sender_name: "L'Entracte",
    sender_email: "",
    beneficiary_email: "",
    message: "",
    payment_method: "cash",
    send_email: false,
    note: "",
  };

  const openManual = () => { setManual({ ...emptyManual }); setManualErr(""); };

  const submitManual = async () => {
    setManualErr(""); setManualSaving(true);
    try {
      const body = { ...manual, amount: Number(manual.amount) || 0 };
      if (!body.sender_email) delete body.sender_email;
      if (!body.beneficiary_email) delete body.beneficiary_email;
      await api.post("/admin/giftcards", body);
      setManual(null);
      setToast("Carte cadeau créée");
      await load();
      setTimeout(() => setToast(""), 3500);
    } catch (e) {
      setManualErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setManualSaving(false);
    }
  };

  const setStatus = async (id, newStatus) => {
    await api.patch(`/admin/giftcards/${id}/status`, { status: newStatus });
    setToast(newStatus === "used" ? "Carte marquée comme utilisée" : "Statut mis à jour");
    load();
    setTimeout(() => setToast(""), 3000);
  };

  const resend = async (id) => {
    if (!window.confirm("Renvoyer la carte cadeau par email ?")) return;
    const r = await api.post(`/admin/giftcards/${id}/resend`);
    setToast(r.data?.ok ? "Email renvoyé" : "Erreur lors du renvoi");
    setTimeout(() => setToast(""), 3000);
  };

  const downloadPdf = (id, code) => {
    const token = localStorage.getItem("entracte_token");
    const url = `${BACKEND}/api/admin/giftcards/${id}/pdf${token ? `?_=${Date.now()}` : ""}`;
    // Use fetch with credentials to get the file
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `carte-cadeau-${code}.pdf`;
        link.click();
        URL.revokeObjectURL(link.href);
      });
  };

  const fmtAmt = (a) => `${Number(a).toFixed(2).replace(/\.00$/, "")} €`;
  const fmtDate = (d) => d ? new Date(d).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—";

  return (
    <div data-testid="admin-giftcards-page">
      <div className="flex justify-between items-start gap-4 flex-wrap">
        <div>
          <p className="overline">Vente en ligne & en salle</p>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Cartes Cadeaux</h1>
          <p className="text-[#697585] mt-2 max-w-xl">Gérez les cartes vendues sur le site, créez-en manuellement (espèces, virement, offerte), marquez-les comme utilisées et téléchargez les PDF.</p>
        </div>
        <button
          data-testid="create-giftcard-btn"
          onClick={openManual}
          className="btn-primary"
        >
          <Plus size={14} /> Créer une carte
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        <StatCard icon={Gift} label="Cartes vendues" value={stats.total_sold} sub={`${stats.active} actives`} />
        <StatCard icon={TrendingUp} label="Chiffre d'affaires" value={`${stats.total_revenue.toLocaleString("fr-FR")} €`} sub="depuis le début" />
        <StatCard icon={CheckCircle2} label="Cartes utilisées" value={stats.used} sub={`${stats.usage_rate}% d'utilisation`} />
        <StatCard icon={Clock} label="En circulation" value={stats.active} sub="à honorer" />
      </div>

      {/* Filters */}
      <div className="mt-10 flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
        <form onSubmit={onSearch} className="flex-1 flex items-center gap-2 border border-[#D9D3C5] bg-[#FFFFFF] px-4">
          <Search size={16} className="text-[#697585]" />
          <input
            data-testid="giftcards-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par code, nom, email…"
            className="flex-1 bg-transparent py-3 text-sm focus:outline-none"
          />
          <button type="submit" className="text-xs uppercase tracking-[0.18em] text-[#B8956A]">Chercher</button>
        </form>
        <div className="flex gap-2 flex-wrap">
          {[
            { v: "", l: "Toutes" },
            { v: "active", l: "Actives" },
            { v: "used", l: "Utilisées" },
            { v: "pending", l: "En attente" },
            { v: "cancelled", l: "Annulées" },
          ].map((b) => (
            <button
              key={b.v}
              data-testid={`giftcards-filter-${b.v || "all"}`}
              onClick={() => setFilter(b.v)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${
                filter === b.v ? "border-[#B8956A] bg-[#B8956A]/10 text-[#203240]" : "border-[#D9D3C5] text-[#697585] hover:border-[#B8956A]/50"
              }`}
            >
              {b.l}
            </button>
          ))}
        </div>
      </div>

      {toast && (
        <div className="mt-6 p-3 border border-[#B8956A]/40 bg-[#B8956A]/10 text-[#203240] text-sm">{toast}</div>
      )}

      {/* List */}
      <div className="mt-8 space-y-4">
        {loading && <p className="text-[#697585]">Chargement…</p>}
        {!loading && items.length === 0 && <p className="text-[#697585]">Aucune carte cadeau.</p>}
        {items.map((gc) => {
          const st = STATUS_LABEL[gc.status] || STATUS_LABEL.pending;
          return (
            <div key={gc.id} data-testid={`giftcard-row-${gc.code}`} className="border border-[#D9D3C5] bg-[#FFFFFF] p-6">
              <div className="flex flex-col lg:flex-row lg:justify-between gap-6">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-mono text-lg tracking-[0.2em] text-[#203240]">{gc.code}</span>
                    <span className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.2em] border ${st.color}`}>{st.label}</span>
                    <span className="font-display text-2xl text-[#B8956A]">{fmtAmt(gc.amount)}</span>
                  </div>
                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 text-sm text-[#697585]">
                    <div><span className="text-[#203240]">Pour :</span> {gc.beneficiary_name}</div>
                    <div><span className="text-[#203240]">De :</span> {gc.sender_name}</div>
                    <div className="flex items-center gap-2"><Mail size={14}/> {gc.sender_email}</div>
                    {gc.beneficiary_email && <div className="flex items-center gap-2"><Users size={14}/> {gc.beneficiary_email}</div>}
                  </div>
                  {gc.message && <p className="mt-3 text-[#203240] italic text-sm">« {gc.message} »</p>}
                  <div className="mt-3 text-xs text-[#697585] flex flex-wrap gap-4">
                    <span>Achetée le {fmtDate(gc.paid_at || gc.created_at)}</span>
                    {gc.used_at && <span>Utilisée le {fmtDate(gc.used_at)}</span>}
                    {gc.email_sent && <span className="text-[#5A7A5A]">✓ Email envoyé</span>}
                  </div>
                </div>

                {gc.payment_status === "paid" && (
                  <div className="flex flex-wrap gap-2 lg:flex-col lg:items-stretch">
                    {gc.status !== "used" ? (
                      <button
                        data-testid={`giftcard-mark-used-${gc.code}`}
                        onClick={() => setStatus(gc.id, "used")}
                        className="btn-primary text-xs py-2"
                      >
                        <CheckCircle2 size={14}/> Marquer utilisée
                      </button>
                    ) : (
                      <button
                        data-testid={`giftcard-mark-active-${gc.code}`}
                        onClick={() => setStatus(gc.id, "active")}
                        className="btn-ghost text-xs py-2"
                      >
                        <RotateCcw size={14}/> Réactiver
                      </button>
                    )}
                    <button
                      data-testid={`giftcard-download-${gc.code}`}
                      onClick={() => downloadPdf(gc.id, gc.code)}
                      className="btn-ghost text-xs py-2"
                    >
                      <Download size={14}/> PDF
                    </button>
                    <button
                      data-testid={`giftcard-resend-${gc.code}`}
                      onClick={() => resend(gc.id)}
                      className="btn-ghost text-xs py-2"
                    >
                      <Mail size={14}/> Renvoyer
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {manual && (
        <ManualGiftCardModal
          value={manual}
          onChange={setManual}
          onClose={() => setManual(null)}
          onSubmit={submitManual}
          error={manualErr}
          saving={manualSaving}
        />
      )}
    </div>
  );
}

function ManualGiftCardModal({ value, onChange, onClose, onSubmit, error, saving }) {
  const set = (k) => (e) =>
    onChange({ ...value, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const PRESETS = [50, 100, 150, 200];
  const METHODS = [
    { v: "cash", label: "Espèces" },
    { v: "bank_transfer", label: "Virement" },
    { v: "check", label: "Chèque" },
    { v: "card_in_person", label: "CB en salle" },
    { v: "complimentary", label: "Offerte (gratuite)" },
    { v: "other", label: "Autre" },
  ];
  return (
    <div className="fixed inset-0 z-[80] bg-black/80 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#FFFFFF] border border-[#D9D3C5] w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="manual-giftcard-modal">
        <div className="flex justify-between items-start mb-6">
          <div>
            <p className="overline">Création manuelle</p>
            <h2 className="font-display text-3xl mt-2">Nouvelle carte cadeau</h2>
            <p className="text-sm text-[#697585] mt-1">Pour les ventes en salle, virements, offres marketing.</p>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        <div className="space-y-6">
          <div>
            <p className="overline">Montant *</p>
            <div className="mt-3 grid grid-cols-4 gap-2">
              {PRESETS.map((p) => (
                <button
                  key={p}
                  type="button"
                  data-testid={`manual-preset-${p}`}
                  onClick={() => onChange({ ...value, amount: p })}
                  className={`py-3 border text-base font-display ${Number(value.amount) === p ? "border-[#B8956A] bg-[#B8956A]/10" : "border-[#D9D3C5] text-[#697585]"}`}
                >{p} €</button>
              ))}
            </div>
            <input
              type="number" min={1} max={5000} step={1}
              value={value.amount}
              onChange={set("amount")}
              data-testid="manual-amount"
              className="field mt-3"
              placeholder="Ou saisir un montant libre"
            />
          </div>

          <div>
            <p className="overline">Bénéficiaire *</p>
            <input className="field mt-3" placeholder="Nom du bénéficiaire" required value={value.beneficiary_name} onChange={set("beneficiary_name")} data-testid="manual-beneficiary-name" />
            <input className="field mt-3" type="email" placeholder="Email du bénéficiaire (optionnel)" value={value.beneficiary_email} onChange={set("beneficiary_email")} data-testid="manual-beneficiary-email" />
            <textarea className="field mt-3" rows={2} maxLength={200} placeholder="Message personnel (optionnel) — apparaîtra sur la carte" value={value.message} onChange={set("message")} data-testid="manual-message" />
          </div>

          <div>
            <p className="overline">De la part de</p>
            <input className="field mt-3" placeholder={`Nom (par défaut "L'Entracte")`} value={value.sender_name} onChange={set("sender_name")} data-testid="manual-sender-name" />
            <input className="field mt-3" type="email" placeholder="Email acheteur (optionnel — pour le reçu)" value={value.sender_email} onChange={set("sender_email")} data-testid="manual-sender-email" />
          </div>

          <div>
            <p className="overline">Mode de paiement</p>
            <select className="field mt-3" value={value.payment_method} onChange={set("payment_method")} data-testid="manual-payment-method">
              {METHODS.map((m) => <option key={m.v} value={m.v} className="bg-[#F6F0E7]">{m.label}</option>)}
            </select>
            <input className="field mt-3" placeholder="Note interne (n° de facture, contexte, etc.)" value={value.note} onChange={set("note")} data-testid="manual-note" />
          </div>

          <div className="border border-[#D9D3C5] p-4 bg-[#F6F0E7]/40">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={value.send_email}
                onChange={set("send_email")}
                className="mt-1 accent-[#B8956A]"
                data-testid="manual-send-email"
              />
              <span className="text-sm text-[#203240]">
                Envoyer la carte par email au bénéficiaire (ou à l'acheteur si pas d'email bénéficiaire).
                <span className="block text-xs text-[#697585] mt-1">
                  Décochez si vous imprimez la carte sur place — vous pourrez télécharger le PDF depuis la liste.
                </span>
              </span>
            </label>
          </div>

          {error && <p data-testid="manual-error" className="text-sm text-red-500 border border-red-200 bg-red-50 p-3">{error}</p>}
        </div>

        <div className="mt-8 flex gap-3 justify-end">
          <button onClick={onClose} className="btn-ghost">Annuler</button>
          <button
            onClick={onSubmit}
            disabled={saving || !value.beneficiary_name || !value.amount}
            className="btn-primary"
            data-testid="manual-submit"
          >
            <Save size={14} /> {saving ? "Création…" : "Créer la carte"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <div className="border border-[#D9D3C5] bg-[#FFFFFF] p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">{label}</p>
          <p className="font-display text-3xl mt-2 text-[#203240]">{value}</p>
          {sub && <p className="text-xs text-[#697585] mt-1">{sub}</p>}
        </div>
        <Icon size={20} className="text-[#B8956A]" />
      </div>
    </div>
  );
}
